/**
 * Appeal Letter Generator — template engine for free draft letters.
 *
 * Generates structured appeal-letter drafts based on the platform, suspension
 * category, account type, and user-provided context. Output is meant as a
 * starting point — the entire site repeatedly states outcomes are not
 * guaranteed and that this is a *first draft*, not a polished final.
 *
 * All template language is sourced from the same neutral policy-reference work
 * we publish across the blog and platform pages. No invented "insider tone,"
 * no recovery promises, no fake scarcity.
 */

const PLATFORM_OPEN = {
  instagram: {
    address: "Instagram Support / Meta Community Operations",
    rules: "Meta Community Standards and Instagram Community Guidelines",
    appealUrl: "https://help.instagram.com/contact/606967319425038",
  },
  facebook: {
    address: "Meta Support / Facebook Account Operations",
    rules: "Meta Community Standards",
    appealUrl: "https://www.facebook.com/help/contact/260749603972907",
  },
  facebook_business: {
    address: "Meta Business Integrity Team",
    rules: "Meta Community Standards, Advertising Standards, and Business Account Policies",
    appealUrl: "https://www.facebook.com/business/help/contact/289407004559500",
  },
  instagram_ad: {
    address: "Meta Business Integrity Team",
    rules: "Meta Advertising Standards",
    appealUrl: "https://www.facebook.com/business/help/contact/2025456854280468",
  },
  tiktok: {
    address: "TikTok Trust & Safety Team",
    rules: "TikTok Community Guidelines",
    appealUrl: "https://www.tiktok.com/legal/report/feedback",
  },
  youtube: {
    address: "YouTube Trust & Safety / Channel Appeals",
    rules: "YouTube Community Guidelines and Terms of Service",
    appealUrl: "https://support.google.com/youtube/contact/tossla",
  },
  x: {
    address: "X Corp. — Appeals Team",
    rules: "X Rules and the X Terms of Service",
    appealUrl: "https://help.x.com/forms/general?subtopic=suspended",
  },
  linkedin: {
    address: "LinkedIn Trust & Safety",
    rules: "LinkedIn Professional Community Policies",
    appealUrl: "https://www.linkedin.com/help/linkedin/answer/a1336988",
  },
  google_business: {
    address: "Google Business Profile Support",
    rules: "Google Business Profile Guidelines",
    appealUrl: "https://support.google.com/business/contact/business_redressal_form",
  },
};

const CATEGORY_FRAMING = {
  community_standards: {
    label: "Community Standards / Community Guidelines violation",
    framing:
      "I respectfully request a human review of this enforcement action. After carefully reading the cited policy, I believe my account activity falls within the documented exceptions and does not violate the spirit of the rules.",
  },
  advertising_standards: {
    label: "Advertising Standards violation",
    framing:
      "I respectfully request a human review of this enforcement action under the Advertising Standards. My business operates within the cited category's documented exceptions, and I am happy to provide additional verification.",
  },
  business_quality: {
    label: "Account Quality / business feedback issue",
    framing:
      "I respectfully request a human review of my account's quality signals. I have taken concrete steps to address the underlying feedback patterns and would like the opportunity to demonstrate the improved trajectory.",
  },
  hacked_compromised: {
    label: "Hacked / compromised account recovery",
    framing:
      "I am the legitimate owner of this account. I am writing to report that the account was accessed without my authorization, and I respectfully request reinstatement of access in my name.",
  },
  inauthentic_behavior: {
    label: "Inauthentic behavior / impersonation flag",
    framing:
      "I am writing to formally dispute an Inauthentic Behavior enforcement action. My account represents my genuine identity and authentic activity, and I am happy to provide identity verification to substantiate this.",
  },
  identity_verification: {
    label: "Identity verification failure",
    framing:
      "I am submitting this appeal alongside the requested identity documentation. The previous verification attempts were unsuccessful due to [specify reason — image quality, name mismatch, etc.], which I have now corrected.",
  },
  ip_dmca: {
    label: "Intellectual property / DMCA enforcement",
    framing:
      "I respectfully submit this counter-notification to the intellectual property complaint filed against my content. The use of the cited material falls within fair use / I am the rightful owner / I have a documented license — and I am providing supporting evidence.",
  },
  business_verification: {
    label: "Business verification failure",
    framing:
      "I am writing to provide complete business verification documentation following the failed verification attempt(s). All requested documentation is included with this submission.",
  },
  cib: {
    label: "Coordinated Inauthentic Behavior flag",
    framing:
      "I am writing to formally dispute the Coordinated Inauthentic Behavior enforcement action. My account operates independently and is not associated with any coordinated network. I am happy to provide identity and ownership verification to substantiate this.",
  },
  spam_generic: {
    label: "Spam / generic Community Standards violation",
    framing:
      "I respectfully request a human review of this enforcement action. The cited 'generic' violation does not appear to map to any specific posted content, and I would appreciate clarification on which specific policy was triggered so I can address it directly.",
  },
};

/**
 * Build the structured appeal-letter draft.
 *
 * Returns three artifacts:
 *   - appealLetter: a first-draft letter the user can edit and submit
 *   - escalationLetter: a longer version suitable for written escalation
 *     (certified mail / formal legal documentation channel)
 *   - evidenceChecklist: items reviewers typically need for this case
 *
 * Output is plain text, ready to copy/paste. Markdown is avoided so the
 * draft can be pasted directly into a platform appeal form without
 * formatting cleanup.
 */
export function generateAppealLetter({
  platform,
  category,
  accountType,
  accountIdentifier,
  noticeDate,
  noticeText,
  priorAppeals,
  revenueImpact,
  userName,
  contactEmail,
}) {
  const platformInfo = PLATFORM_OPEN[platform] || PLATFORM_OPEN.instagram;
  const categoryInfo = CATEGORY_FRAMING[category] || CATEGORY_FRAMING.community_standards;

  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const accountLine = accountIdentifier
    ? `Account / Username: ${accountIdentifier}`
    : "Account / Username: [INSERT USERNAME OR ACCOUNT ID]";
  const dateLine = noticeDate
    ? `Date of enforcement notice: ${noticeDate}`
    : "Date of enforcement notice: [INSERT DATE]";
  const nameLine = userName ? userName : "[YOUR FULL LEGAL NAME]";
  const emailLine = contactEmail ? contactEmail : "[YOUR CONTACT EMAIL]";

  const noticeSummary = noticeText
    ? `The enforcement notice I received states, in part: "${noticeText.trim().slice(0, 500)}${noticeText.length > 500 ? "..." : ""}"`
    : `[Quote the relevant portion of the enforcement notice here. Use the exact language the platform used — this matters for reviewer pattern-matching.]`;

  const priorAppealsLine =
    priorAppeals === true
      ? `Prior appeal submissions: Yes. I have submitted a previous appeal which was reviewed (or auto-denied). I am submitting this appeal with additional documentation and a more specific policy-mapped argument.`
      : priorAppeals === false
      ? `Prior appeal submissions: This is my first formal appeal of this enforcement action.`
      : `Prior appeal submissions: [Indicate whether you've appealed before, and what the outcome was.]`;

  const revenueLine =
    revenueImpact === true
      ? `Business impact: This account is operationally critical. The enforcement action is causing measurable business impact (lost sales, paused advertising, blocked customer communication). I am happy to provide documentation of the impact if helpful.`
      : "";

  // -----------------------------------------------------------------
  // APPEAL LETTER (first draft)
  // -----------------------------------------------------------------
  const appealLetter = `${today}

To: ${platformInfo.address}
Re: Appeal of Enforcement Action — ${categoryInfo.label}
Submitted via: ${platformInfo.appealUrl}

To Whom It May Concern,

I am writing to respectfully appeal an enforcement action taken against my account. Below is the relevant case information:

  ${accountLine}
  Account Type: ${accountType || "[personal / creator / business / ad account / business manager]"}
  ${dateLine}

${noticeSummary}

${categoryInfo.framing}

After reviewing the ${platformInfo.rules}, I would like to respectfully highlight the following points:

  1. [Cite the specific policy section the enforcement claims to apply.]
  2. [Explain how your content / behavior either does NOT violate that section, or falls into a documented exception. Be specific. Reference exact policy language where possible.]
  3. [Provide context: account history, length of good-standing use, the legitimate purpose of any cited content.]
  4. [If applicable, acknowledge any prior issues openly and explain what has changed.]

${priorAppealsLine}

${revenueLine}

Supporting documentation accompanies this appeal:

  • [List each evidence item you are including. Examples: government-issued ID, business registration, screenshots, prior correspondence, account history.]
  • [Be specific — "Screenshot of enforcement notice dated ${noticeDate || "[date]"}" rather than "screenshots".]

I respectfully request a human review of this appeal. I am committed to operating within ${platformInfo.rules} and would welcome any clarification on how to remain compliant going forward.

Thank you for your time and consideration.

Sincerely,
${nameLine}
${emailLine}

---

NOTE FROM SOCIAL DISPUTE CO:
This is a FIRST DRAFT generated from your inputs. Before submitting:
  1. Replace every bracketed [INSERT ...] placeholder with specific information.
  2. Items 1–4 above are the most important — be specific to YOUR case.
  3. Read the Community Standards / Rules section the platform cited. Quote the
     exact policy language where you can.
  4. Keep the tone professional. Avoid emotional language, demands, or
     accusations.
  5. Outcomes are determined entirely by the platform. There are no guarantees.

If you'd like a professionally-prepared appeal package built specifically for
your case (with policy-mapped argument, evidence index, and submission
support), see https://socialdisputeco.com/pricing.
`;

  // -----------------------------------------------------------------
  // ESCALATION LETTER (longer, for written / certified mail escalation)
  // -----------------------------------------------------------------
  const escalationLetter = `${today}

VIA CERTIFIED MAIL — RETURN RECEIPT REQUESTED
[Optional: also email to platform's published legal/policy contact]

[PLATFORM ENTITY LEGAL DEPARTMENT — see /platform-registered-agent-addresses for current mailing addresses]

Re: FORMAL ESCALATION — Account Enforcement Appeal
    ${accountLine}

Dear Sir or Madam,

I am writing to formally escalate an unresolved appeal regarding an enforcement
action taken against my account on your platform. Despite previously submitting
the standard appeal through your published channels, I have not received a
substantive response, and the matter remains unresolved.

ACCOUNT INFORMATION
  ${accountLine}
  Account Type: ${accountType || "[personal / creator / business / ad account / business manager]"}
  ${dateLine}
  Enforcement Category: ${categoryInfo.label}

ENFORCEMENT NOTICE
${noticeSummary}

PROCEDURAL HISTORY
${priorAppealsLine}

I have attached or include herein:
  • Copy of the original enforcement notice
  • Copy of any prior appeal submission(s) and any responses received
  • Government-issued identification verifying my identity
  • [Other documentation specific to your case]

LEGAL BASIS FOR ESCALATION
I respectfully request that this matter be reviewed by a human at your Trust &
Safety / Legal team rather than processed by automated systems. The enforcement
action appears to be inconsistent with ${platformInfo.rules} as published, and
I have not been given a meaningful opportunity to respond to the specific
allegations.

${revenueImpact === true ? `BUSINESS IMPACT
This account is operationally critical to my business. The continued enforcement
action is causing ongoing, documentable financial harm. I am prepared to
quantify this impact upon request.

` : ""}REMEDY REQUESTED
I respectfully request:
  1. A human review of this enforcement action;
  2. A specific citation of the policy section allegedly violated;
  3. An opportunity to respond to that specific citation with documentation;
  4. Reinstatement of account access pending the outcome of that review.

I am happy to provide any additional documentation reasonably requested. Please
direct any correspondence to:

  ${nameLine}
  ${emailLine}
  [YOUR MAILING ADDRESS — for certified mail correspondence]

Thank you for your time and attention.

Sincerely,

${nameLine}

---

NOTE FROM SOCIAL DISPUTE CO:
This is a FIRST DRAFT for a formal written escalation. Use this path only when:
  • You have already submitted the standard in-app appeal and either received a
    denial or received no response within the documented review window.
  • The account is of meaningful business or personal value.
  • You are prepared to follow up if the platform's legal team responds.

This letter is NOT a legal threat or a demand. It is a polite, professional
escalation. Threats and demands typically reduce reviewer responsiveness — we
strongly advise against them.

For platform legal department mailing addresses (Meta, X, TikTok, etc.), see
https://socialdisputeco.com/platform-registered-agent-addresses.

If you want this escalation professionally prepared with platform-specific
language and the right legal contact addresses, see
https://socialdisputeco.com/pricing.
`;

  // -----------------------------------------------------------------
  // EVIDENCE CHECKLIST (per category + account type)
  // -----------------------------------------------------------------
  const base = [
    "Screenshot of the full enforcement notice (with timestamp visible)",
    "Government-issued photo ID matching the account name",
    "Account creation date and original registration email/phone",
    "Screenshots of recent posts/content cited in the notice (if specific content was named)",
    "Timeline of relevant account activity in the 30 days before the action",
  ];
  const business = [
    "Business registration documentation (LLC Articles of Organization, Inc. paperwork)",
    "EIN letter from the IRS (or equivalent foreign tax ID)",
    "Business license / occupational permit (if applicable)",
    "Domain ownership proof for any advertised domains",
    "Most recent billing statement (for ad-account cases)",
  ];
  const hacked = [
    "Original email address and phone number linked to the account (with proof of access if changed)",
    "Approximate account creation date",
    "List of followers/posts/username known to the platform",
    "Any password-reset emails or security alerts received during the hack window",
  ];
  const ip = [
    "Proof of ownership / license for the cited content (registration, license agreement, contract)",
    "Documentation of fair-use criteria if relevant (purpose, nature, amount, market effect)",
  ];

  const checklist = [...base];
  if (["business", "ad_account", "business_manager"].includes(accountType)) {
    checklist.push(...business);
  }
  if (category === "hacked_compromised") {
    checklist.push(...hacked);
  }
  if (category === "ip_dmca") {
    checklist.push(...ip);
  }

  return {
    appealLetter,
    escalationLetter,
    evidenceChecklist: checklist,
    platformInfo,
    categoryInfo,
  };
}

export const PLATFORM_OPTIONS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "facebook_business", label: "Facebook Business Manager" },
  { id: "instagram_ad", label: "Instagram Ad Account" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "x", label: "X (Twitter)" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "google_business", label: "Google Business Profile" },
];

export const CATEGORY_OPTIONS = [
  { id: "community_standards", label: "Community Standards / Community Guidelines violation" },
  { id: "advertising_standards", label: "Advertising Standards violation" },
  { id: "business_quality", label: "Account Quality / business feedback" },
  { id: "hacked_compromised", label: "Hacked / compromised account" },
  { id: "inauthentic_behavior", label: "Inauthentic behavior / impersonation" },
  { id: "identity_verification", label: "Identity verification failure" },
  { id: "business_verification", label: "Business verification failure" },
  { id: "ip_dmca", label: "Intellectual property / DMCA / Copyright" },
  { id: "cib", label: "Coordinated Inauthentic Behavior" },
  { id: "spam_generic", label: "Spam / generic violation (no specific policy cited)" },
];

export const ACCOUNT_TYPE_OPTIONS = [
  { id: "personal", label: "Personal account" },
  { id: "creator", label: "Creator / influencer" },
  { id: "business", label: "Small business / brand" },
  { id: "ad_account", label: "Ad account" },
  { id: "business_manager", label: "Business Manager / agency" },
];
